import { useEffect, useRef, useState } from 'react';
import { OTPSignature } from './OTPSignature';
import { FileSignature, FileText, Eye, CheckCircle2, AlertCircle, ScrollText } from "lucide-react";
import { CONTRACT_TRANSLATIONS } from './constants';
import { usePdfReadConfirmation } from './hooks/usePdfReadConfirmation';
import { ScrollablePdfReader } from './ScrollablePdfReader';
import { getCustomerDisplayName } from '../../utils/customerName';

// Req. A. Zorzutti / LoJack Legal Stand-up 05/06/2026:
//   "Teniamo lo sniffer sullo scroll down dell'utente nella versione
//    desktop, mentre facciamo il downgrade al countdown SOLO nella
//    versione mobile per evitare possibili malfunzionamenti."
//
// Implementation: a shared React hook `usePdfReadConfirmation`
// detects the device class at mount (combining viewport-width,
// touch-capability and userAgent), then:
//   - DESKTOP → listens to scroll events on the iframe's
//     document and unlocks the "Ho letto" checkbox once the
//     customer reaches the bottom of the PDF.
//   - MOBILE  → falls back to the previous 15s countdown, since
//     iOS Safari / Android Chrome native PDF viewers don't
//     expose scroll events to the parent.
// The previous fixed-countdown implementation was REPLACED on
// the contract modal (this file) and on `GDPRPreferences.js`
// (privacy policy section).
const MIN_READ_SECONDS = 15;

export const ContractModal = ({
  showTerms,
  setShowTerms,
  customer,
  customerId,
  termsAccepted,
  setTermsAccepted,
  contractPdfUrl,
  signingContract,
  setSigningContract,
  setContractSigned,
  fetchCustomer,
  inline = false,
  // UI language scelta dal cliente nello step "form" — propaga in
  // cascata per i18n della UI (titoli, label, bottoni). NON va passata
  // ai PDF: i PDF (contratto + privacy) seguono sempre la
  // ``customer.country`` (lingua dealer-assigned). Req. Fabrizio
  // 10/06/2026 — vedi /app/memory/PRD.md.
  uiLanguage,
  // Bug fix Fabrizio 28/02/2026: il canale OTP mostrato al cliente
  // (mail / mobile / entrambi) ora segue la Per-Country Policy
  // gestita dall'admin (`country_config.otp_delivery_channel`).
  // Default a 'both' per back-compat se il country non ha policy.
  otpDeliveryChannel = 'both',
  // Optional callback (Mara/Zorzutti req. 24/07/2026): parent receives
  // live read-progress % (0-100) so the outer SigningStepper can display
  // a real-time "N% del contratto letto" badge + progress bar.
  onReadProgress,
}) => {
  // Source-of-truth: usa la lingua UI scelta se presente, altrimenti
  // ricade sulla lingua dealer (``customer.country``). Tutti gli usi
  // i18n nella UI passano da qui — gli URL ai PDF restano invariati.
  const langCode = (uiLanguage || customer?.country || 'IT').toUpperCase();
  // Granular audit timestamp — captured the moment the customer
  // ticks the General-Terms checkbox; forwarded to verify-otp.
  const [termsAcceptedAt, setTermsAcceptedAt] = useState(null);
  const iframeRef = useRef(null);
  // Scrollable wrapper ref used by the new react-pdf reader path (desktop).
  // Mobile keeps the legacy iframe path so we don't trip up native PDF
  // viewers — see usePdfReadConfirmation for the decision matrix.
  const pdfScrollContainerRef = useRef(null);
  // Same desktop-vs-mobile heuristic the hook uses, so we can pick the
  // correct PDF rendering strategy (react-pdf wrapper vs legacy iframe).
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === "undefined") return true;
    try {
      const narrow = window.matchMedia?.("(max-width: 768px)")?.matches;
      const touch = "ontouchstart" in window;
      const ios = /iPad|iPhone|iPod/i.test(navigator.userAgent || "");
      return !(narrow || touch || ios);
    } catch (_) { return true; }
  });
  useEffect(() => {
    const onResize = () => {
      try {
        const narrow = window.matchMedia?.("(max-width: 768px)")?.matches;
        const touch = "ontouchstart" in window;
        const ios = /iPad|iPhone|iPod/i.test(navigator.userAgent || "");
        setIsDesktop(!(narrow || touch || ios));
      } catch (_) { setIsDesktop(true); }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Read-confirmation hook (req. A. Zorzutti 05/06/2026 + Fabrizio
  // 11/06/2026 scroll-reliability fix). Desktop uses scroll-end detection
  // on the react-pdf wrapper, mobile falls back to a 15s countdown.
  const {
    mode: readMode,
    readDone: readTimerComplete,
    secondsRemaining: remaining,
    progressPct: readProgress,
    scrolledToEnd,
  } = usePdfReadConfirmation({
    iframeRef,                   // legacy fallback (mobile path)
    scrollContainerRef: pdfScrollContainerRef, // preferred (desktop path)
    minSeconds: MIN_READ_SECONDS,
    enabled: showTerms,
  });

  // Emit live progress to parent (SigningStepper progress bar)
  useEffect(() => {
    if (typeof onReadProgress === 'function') onReadProgress(readProgress || 0);
  }, [readProgress, onReadProgress]);

  // Haptic pulse on the "Accetto" checkbox when the read timer completes
  // (Fabrizio req. 24/07/2026): fires ONCE via an edge-trigger detected by
  // the ref, so re-renders after read-completion don't loop the animation.
  // The animation itself is Tailwind `animate-haptic-pulse` (3× ~1.2s).
  const [checkboxPulse, setCheckboxPulse] = useState(false);
  const pulseTriggeredRef = useRef(false);
  useEffect(() => {
    if (readTimerComplete && !pulseTriggeredRef.current && !termsAccepted) {
      pulseTriggeredRef.current = true;
      setCheckboxPulse(true);
      // Total animation duration = 0.4s × 3 iterations = 1.2s + 200ms buffer
      const t = setTimeout(() => setCheckboxPulse(false), 1400);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [readTimerComplete, termsAccepted]);

  // Whitelabel guard (Fabrizio req. 01/06/2026): in produzione il
  // frontend cliente (securewallet.lojack.eu) e il backend sono su
  // domini diversi → un link <a target=_blank> al backend esporrebbe
  // l'host interno di hosting nella barra URL. Soluzione: fetch del
  // PDF + ObjectURL su Blob, così iframe + apertura in nuova tab
  // usano un URL `blob:` appartenente al dominio frontend.
  const [contractBlobUrl, setContractBlobUrl] = useState(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);

  const previewUrl = customerId
    ? `${process.env.REACT_APP_BACKEND_URL || ''}/api/contracts/preview/${customerId}`
    : null;

  useEffect(() => {
    if (!contractPdfUrl) {
      setContractBlobUrl(null);
      return undefined;
    }
    let cancelled = false;
    let createdUrl = null;
    (async () => {
      try {
        const resp = await fetch(contractPdfUrl, { credentials: 'omit' });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        if (cancelled) return;
        const pdfBlob = blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
        createdUrl = URL.createObjectURL(pdfBlob);
        setContractBlobUrl(createdUrl);
      } catch (_) {
        if (!cancelled) setContractBlobUrl(contractPdfUrl);
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [contractPdfUrl]);

  useEffect(() => {
    if (!previewUrl) {
      setPreviewBlobUrl(null);
      return undefined;
    }
    let cancelled = false;
    let createdUrl = null;
    (async () => {
      try {
        const resp = await fetch(previewUrl, { credentials: 'omit' });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        if (cancelled) return;
        const pdfBlob = blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
        createdUrl = URL.createObjectURL(pdfBlob);
        setPreviewBlobUrl(createdUrl);
      } catch (_) {
        if (!cancelled) setPreviewBlobUrl(previewUrl);
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [previewUrl]);

  const contractDisplaySrc = contractBlobUrl || contractPdfUrl;
  const previewDisplaySrc = previewBlobUrl || previewUrl;

  if (!showTerms) return null;

  const ct = CONTRACT_TRANSLATIONS[langCode] || CONTRACT_TRANSLATIONS['IT'];
  const dateLocale = {
    'IT':'it-IT','UK':'en-GB','FR':'fr-FR','ES':'es-ES','DE':'de-DE','NL':'nl-NL',
    'PT':'pt-PT','PL':'pl-PL','SE':'sv-SE','NO':'nb-NO','FI':'fi-FI','DK':'da-DK',
    'CZ':'cs-CZ','GR':'el-GR','CH':'de-CH','BE':'fr-BE','GBZ':'en-GB','IRL':'en-IE'
  }[langCode] || 'it-IT';

  // Localized strings for the new "Ho letto" flow (#23) — all 18 countries.
  const ackLabels = {
    IT: { ack: "Confermo di aver letto l'intero contratto", wait: "Attendi qualche secondo per finire di leggere il contratto", scrollHint: "Scorri il contratto fino in fondo per poter confermare la lettura", done: "Lettura confermata - puoi procedere con la firma" },
    UK: { ack: "I confirm I have read the entire contract", wait: "Please wait a few seconds to finish reading the contract", scrollHint: "Scroll the contract to the bottom to confirm you have read it", done: "Reading confirmed - you can now proceed with the signature" },
    FR: { ack: "Je confirme avoir lu l'intégralité du contrat", wait: "Veuillez attendre quelques secondes pour terminer la lecture", scrollHint: "Faites défiler le contrat jusqu'en bas pour confirmer la lecture", done: "Lecture confirmée - vous pouvez procéder à la signature" },
    ES: { ack: "Confirmo haber leído todo el contrato", wait: "Espere unos segundos para terminar de leer el contrato", scrollHint: "Desplázate hasta el final del contrato para confirmar la lectura", done: "Lectura confirmada - puede proceder con la firma" },
    DE: { ack: "Ich bestätige, den gesamten Vertrag gelesen zu haben", wait: "Bitte warten Sie einige Sekunden, um den Vertrag zu Ende zu lesen", scrollHint: "Scrollen Sie den Vertrag bis zum Ende, um die Lektüre zu bestätigen", done: "Lektüre bestätigt - Sie können mit der Unterschrift fortfahren" },
    PL: { ack: "Potwierdzam, że przeczytałem całą umowę", wait: "Poczekaj kilka sekund, aby dokończyć czytanie umowy", scrollHint: "Przewiń umowę do końca, aby potwierdzić przeczytanie", done: "Czytanie potwierdzone - możesz przystąpić do podpisania" },
    PT: { ack: "Confirmo ter lido todo o contrato", wait: "Aguarde alguns segundos para terminar de ler o contrato", scrollHint: "Role o contrato até ao fim para confirmar a leitura", done: "Leitura confirmada - pode prosseguir com a assinatura" },
    NL: { ack: "Ik bevestig dat ik het hele contract heb gelezen", wait: "Wacht een paar seconden om het contract uit te lezen", scrollHint: "Scroll naar het einde van het contract om de lezing te bevestigen", done: "Lezing bevestigd - u kunt doorgaan met ondertekenen" },
    SE: { ack: "Jag bekräftar att jag har läst hela avtalet", wait: "Vänta några sekunder för att slutföra läsningen", scrollHint: "Bläddra ner till slutet av avtalet för att bekräfta läsningen", done: "Läsning bekräftad - du kan fortsätta med signaturen" },
    NO: { ack: "Jeg bekrefter at jeg har lest hele kontrakten", wait: "Vennligst vent noen sekunder for å fullføre lesingen", scrollHint: "Bla ned til slutten av kontrakten for å bekrefte at du har lest den", done: "Lesing bekreftet - du kan fortsette med signaturen" },
    FI: { ack: "Vahvistan, että olen lukenut koko sopimuksen", wait: "Odota muutama sekunti viimeistelläksesi lukemisen", scrollHint: "Vieritä sopimus loppuun vahvistaaksesi lukemisen", done: "Lukeminen vahvistettu - voit jatkaa allekirjoitukseen" },
    DK: { ack: "Jeg bekræfter at have læst hele kontrakten", wait: "Vent et par sekunder for at færdiggøre læsningen", scrollHint: "Rul kontrakten til bunden for at bekræfte læsningen", done: "Læsning bekræftet - du kan fortsætte med underskriften" },
    CZ: { ack: "Potvrzuji, že jsem si přečetl celou smlouvu", wait: "Počkejte několik sekund, abyste dokončili čtení smlouvy", scrollHint: "Posuňte smlouvu až dolů pro potvrzení přečtení", done: "Čtení potvrzeno - můžete pokračovat s podpisem" },
    GR: { ack: "Επιβεβαιώνω ότι έχω διαβάσει ολόκληρη τη σύμβαση", wait: "Περιμένετε λίγα δευτερόλεπτα για να ολοκληρώσετε την ανάγνωση", scrollHint: "Κάντε κύλιση της σύμβασης μέχρι το τέλος για να επιβεβαιώσετε την ανάγνωση", done: "Η ανάγνωση επιβεβαιώθηκε - μπορείτε να προχωρήσετε στην υπογραφή" },
    CH: { ack: "Ich bestätige, den gesamten Vertrag gelesen zu haben", wait: "Bitte warten Sie einige Sekunden, um den Vertrag zu Ende zu lesen", scrollHint: "Scrollen Sie den Vertrag bis zum Ende, um die Lektüre zu bestätigen", done: "Lektüre bestätigt - Sie können mit der Unterschrift fortfahren" },
    BE: { ack: "Je confirme avoir lu l'intégralité du contrat", wait: "Veuillez attendre quelques secondes pour terminer la lecture", scrollHint: "Faites défiler le contrat jusqu'en bas pour confirmer la lecture", done: "Lecture confirmée - vous pouvez procéder à la signature" },
    GBZ: { ack: "I confirm I have read the entire contract", wait: "Please wait a few seconds to finish reading the contract", scrollHint: "Scroll the contract to the bottom to confirm you have read it", done: "Reading confirmed - you can now proceed with the signature" },
    IRL: { ack: "I confirm I have read the entire contract", wait: "Please wait a few seconds to finish reading the contract", scrollHint: "Scroll the contract to the bottom to confirm you have read it", done: "Reading confirmed - you can now proceed with the signature" },
  };
  const ackText = ackLabels[langCode] || ackLabels.UK;

  const content = (
    <div className={inline ? "wallet-card" : "modal-content max-w-4xl max-h-[95vh] overflow-y-auto flex flex-col"} onClick={(e) => e.stopPropagation()} data-testid="terms-modal">
      <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
        <FileSignature className="text-lojack-red" />
        {ct.title}
      </h2>

      <p className="text-gray-400 mb-4">{ct.subtitle}</p>

      {/* Read-confirmation banner — spostato SOPRA il documento e senza
          checkbox di sola presa visione (req. Zorzutti mockup 08/07/2026:
          lo scroll completo sblocca direttamente il checkbox unico di
          "presa visione e accettazione" più in basso). */}
      <div
        className={`mb-4 p-4 rounded-xl border-2 transition-colors ${
          readTimerComplete
            ? 'border-lojack-blue-500/30 bg-lojack-blue-500/5'
            : 'border-gray-700 bg-gray-800/50'
        }`}
        data-testid="contract-read-panel"
      >
        {readTimerComplete ? (
          <div className="flex items-center gap-2 text-lojack-blue-400">
            <CheckCircle2 size={18} />
            <span className="text-sm font-medium">{ackText.done}</span>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 text-xs text-yellow-300 font-medium">
              {readMode === 'scroll' ? (
                <>
                  <ScrollText size={14} className="text-yellow-400" />
                  <span>{ackText.scrollHint || ackText.wait}</span>
                </>
              ) : (
                <>
                  <AlertCircle size={14} className="text-yellow-400" />
                  <span>{ackText.wait}</span>
                  <span className="ml-auto font-mono text-yellow-200">{remaining}s</span>
                </>
              )}
            </div>
            {/* Progress bar — giallo armonizzato con lo Step 2 (req. Zorzutti 07/07/2026) */}
            <div className="mt-2 h-1 bg-gray-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-yellow-400 transition-all"
                style={{ width: `${readProgress}%` }}
                data-testid="contract-read-progress"
              />
            </div>
          </>
        )}
      </div>

      {/* PDF Viewer Section — embedded iframe + reading timer */}
      {contractPdfUrl && (
        <div className="mb-4">
          <div className="bg-gray-800 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-lojack-red" />
                <span className="text-white font-semibold text-sm">{ct.docTitle}</span>
              </div>
              <a
                href={previewDisplaySrc || contractDisplaySrc}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
                data-testid="view-contract-pdf-btn"
              >
                <Eye size={12} />
                {ct.viewPdf}
              </a>
            </div>

            {/* PDF rendering — desktop uses react-pdf inside a real HTML
                scrollable container (so we can RELIABLY detect when the
                customer reached the bottom of the document, Fabrizio req.
                11/06/2026). Mobile keeps the legacy <iframe> path because
                Android Chrome / iOS Safari rely on the native PDF plugin
                and react-pdf canvas rendering is laggy on low-end phones —
                they still get the 15s countdown safety.

                Fabrizio req. 22/02/2026: the embedded preview now shows
                the FULL personalized 8-page contract (customer data +
                General Conditions + 3 annexes) instead of the standalone
                General Conditions PDF. Rationale: customers were only
                scrolling the first embedded document and missing the
                7 additional pages that reached them only via a separate
                "Visualizza contratto completo" button. Rendering the
                full contract inline also strengthens the informed
                consent trail — the customer literally reads THE
                document they are about to sign. Fallback to
                `contractPdfUrl` (General Conditions) if the
                per-customer preview endpoint is not yet reachable
                (network hiccup) so the reader never shows a blank pane.

                IMPORTANT: react-pdf needs a fetchable URL — pass the raw
                backend URL (NOT the same-origin blob: variant), because
                our CSP `connect-src 'self' https: wss:` blocks blob: XHR. */}
            {isDesktop ? (
              <ScrollablePdfReader
                ref={pdfScrollContainerRef}
                fileUrl={previewUrl || contractPdfUrl}
                height="60vh"
                minHeight={400}
                errorFallbackHref={previewDisplaySrc || contractDisplaySrc}
                errorFallbackLabel={ct.viewPdf}
                loadingLabel={
                  ({ IT: 'Caricamento PDF...', UK: 'Loading PDF...', FR: 'Chargement du PDF...',
                     ES: 'Cargando PDF...', DE: 'PDF wird geladen...', PL: 'Ładowanie PDF...' })[langCode]
                  || 'Loading PDF...'
                }
                errorLabel={
                  ({ IT: 'Impossibile caricare il PDF.', UK: 'Unable to load the PDF.',
                     FR: 'Impossible de charger le PDF.', ES: 'No se puede cargar el PDF.',
                     DE: 'PDF kann nicht geladen werden.', PL: 'Nie można załadować pliku PDF.' })[langCode]
                  || 'Unable to load the PDF.'
                }
                retryLabel={
                  ({ IT: 'Riprova', UK: 'Retry', FR: 'Réessayer',
                     ES: 'Reintentar', DE: 'Erneut versuchen', PL: 'Spróbuj ponownie' })[langCode]
                  || 'Retry'
                }
              />
            ) : (
              <iframe
                ref={iframeRef}
                src={`${previewDisplaySrc || contractDisplaySrc}#toolbar=0&navpanes=0`}
                title="Contratto LoJack"
                className="w-full rounded-lg border border-gray-700 bg-white"
                style={{ height: '60vh', minHeight: '400px' }}
                data-testid="contract-pdf-iframe"
              />
            )}
          </div>
        </div>
      )}

      {/* Customer Info */}
      <div className="bg-gray-800 rounded-lg p-4 mb-4">
        <h4 className="font-semibold text-white mb-3 text-sm">{ct.dataTitle}</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div className="flex flex-col">
            <span className="text-gray-500 text-xs mb-1">{ct.name}</span>
            <span className="text-white font-medium" data-testid="contract-customer-name">{getCustomerDisplayName(customer) || '—'}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-gray-500 text-xs mb-1">{ct.email}</span>
            <span className="text-white font-medium break-all">{customer?.email}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-gray-500 text-xs mb-1">{ct.vehicle}</span>
            <span className="text-white font-medium" data-testid="contract-vehicle-plate">{customer?.vehicle_plate || 'N/A'}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-gray-500 text-xs mb-1">{ct.vin}</span>
            <span className="text-white font-medium font-mono text-xs sm:text-sm break-all" data-testid="contract-vehicle-vin">{customer?.vehicle_vin || 'N/A'}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-gray-500 text-xs mb-1">{ct.date}</span>
            <span className="text-white font-medium">{new Date().toLocaleDateString(dateLocale)}</span>
          </div>
        </div>
      </div>

      {/* OTP Signature Section — only after read ack + terms ack */}
      <div className="border-t border-gray-700 pt-4">
        {/* Fabrizio req. 24/07/2026: haptic-pulse the "Accetto" checkbox
            when the read timer completes so the customer's eye snaps to
            the freshly-unlocked CTA. Wrapper also gets a subtle red
            highlight ring while pulsing, which fades out after 1.4s. */}
        <div
          className={`flex items-start gap-3 mb-4 rounded-lg transition-all duration-500 ${
            checkboxPulse
              ? 'animate-haptic-pulse bg-lojack-red/10 ring-2 ring-lojack-red/60 shadow-[0_0_16px_rgba(220,38,38,0.35)] p-2 -m-2'
              : ''
          }`}
          data-testid="accept-terms-row"
          data-pulse-active={checkboxPulse ? 'true' : 'false'}
        >
          <input
            type="checkbox"
            id="accept-terms"
            className={`checkbox-lojack mt-1 disabled:opacity-40 transition-all duration-300 ${
              readTimerComplete && !termsAccepted
                ? 'ring-2 ring-lojack-red/70 ring-offset-2 ring-offset-gray-900 rounded'
                : ''
            }`}
            checked={termsAccepted}
            disabled={!readTimerComplete}
            onChange={(e) => {
              const checked = e.target.checked;
              setTermsAccepted(checked);
              // Granular audit trail (req. Fabrizio 09/06/2026):
              // capture the exact UI moment the customer ticked
              // (or un-ticked) the General Terms checkbox. The
              // timestamp is forwarded to verify-otp and persisted
              // in signed_contracts.terms_accepted_at.
              if (checked) {
                setTermsAcceptedAt(new Date().toISOString());
              } else {
                setTermsAcceptedAt(null);
              }
            }}
            data-testid="terms-checkbox"
          />
          <label
            htmlFor="accept-terms"
            className={`text-sm transition-colors duration-300 ${
              readTimerComplete
                ? 'text-white font-medium'
                : 'text-gray-600'
            }`}
          >
            {ct.checkbox}
          </label>
        </div>

        {readTimerComplete && termsAccepted && (
          <OTPSignature
            customerId={customerId}
            customerEmail={customer?.email}
            customerPhone={customer?.phone ? `${customer?.phone_prefix || '+39'} ${customer?.phone}` : null}
            country={langCode}
            otpDeliveryChannel={otpDeliveryChannel}
            termsAcceptedAt={termsAcceptedAt}
            onSuccess={() => {
              // Bug fix Fabrizio 2026-06-09 (post-OTP form flicker mistaken
              // for the "Conferma Dati" popup): notify the parent that the
              // contract is signed and let it transition walletStep to
              // 'complete'. We MUST NOT also call setShowTerms(false) here
              // because the parent maps it to setWalletStep('form'), which
              // briefly re-renders the form step before fetchCustomer
              // reconciles to 'complete' (~1s of visible flicker).
              setContractSigned(true);
              fetchCustomer();
            }}
            setSigningContract={setSigningContract}
          />
        )}

        {(!readTimerComplete || !termsAccepted) && (
          <p className="text-gray-500 text-sm text-center py-4">
            {ct.acceptFirst}
          </p>
        )}
      </div>

      {signingContract && (
        <div className="flex items-center justify-center py-4">
          <div className="spinner mr-3"></div>
          <span className="text-gray-400">{ct.saving}</span>
        </div>
      )}

      <div className="flex gap-3 mt-4 pt-4 border-t border-gray-700">
        <button
          onClick={() => setShowTerms(false)}
          className="flex-1 btn-lojack-outline"
          disabled={signingContract}
          data-testid="contract-back-btn"
        >
          {inline ? (ct.back || ct.cancel) : ct.cancel}
        </button>
      </div>

      <p className="text-gray-500 text-xs text-center mt-4">{ct.footer}</p>
    </div>
  );

  if (inline) return content;

  return (
    // Req. A. Zorzutti Stand-up 04/06/2026 pt. 10 — il click sul
    // backdrop NON deve più chiudere il modale firma. Su mobile è
    // facile toccare inavvertitamente l'area esterna e tornare al
    // form perdendo lo stato di lettura. La chiusura ora avviene
    // SOLO via il pulsante "Cancella firma" (sopra) o tasto ESC.
    <div
      className="modal-overlay"
      onClick={(e) => e.stopPropagation()}
      data-testid="contract-modal-overlay"
    >
      {content}
    </div>
  );
};
